import { weekdayLong, weekdayShort } from "@repo/client/format";
import type { PreferredSplit } from "@repo/contract/training";
import {
  "profile-editors.days_tap" as daysTap,
  "profile-editors.fixed_count" as fixedCount,
  "profile-editors.flex_hint_none" as flexHintNone,
  "profile-editors.flex_hint_some" as flexHintSome,
  "profile-editors.flex_label" as flexLabel,
  "profile-editors.flex_title_none" as flexTitleNone,
  "profile-editors.flex_title_some" as flexTitleSome,
  "profile-editors.per_week" as perWeek,
  "profile-editors.preview" as previewTitle,
  "profile-editors.same_all" as sameAll,
  "profile-editors.same_all_hint" as sameAllHint,
  "profile-editors.same_days_note" as sameDaysNote,
  "profile-editors.same_none" as sameNone,
  "profile-editors.same_none_hint" as sameNoneHint,
  "onboarding.days_fixed.question" as sameQuestion,
  "profile-editors.same_some" as sameSome,
  "profile-editors.same_some_hint" as sameSomeHint,
  "profile-editors.some_needs_both" as someNeedsBoth,
  "profile-editors.split_auto" as splitAuto,
  "profile-editors.split_auto_hint" as splitAutoHint,
  "profile-editors.split_change_note" as splitChangeNote,
  "profile-editors.split_hint_full_body" as splitHintFullBody,
  "profile-editors.split_hint_ppl" as splitHintPpl,
  "profile-editors.split_hint_ppl_arms" as splitHintPplArms,
  "profile-editors.split_hint_upper_lower" as splitHintUpperLower,
  "profile-editors.split_question" as splitQuestion,
  "profile-editors.split_recommended" as splitRecommended,
  "profile-editors.split_why" as splitWhy,
  "profile-editors.too_few" as tooFew,
  "profile-editors.too_many" as tooMany,
  "profile-editors.workout_line" as workoutLine,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { ArrowRight, Check } from "lucide-react";
import { useCallback } from "react";
import { cx } from "../../../lib/cx";
import { ChoiceList, Icon, Stepper } from "../../../ui/controls";
import { splitName } from "../../plan/split-name";
import { dayName, type LabelledOption } from "./options";
import { FieldLabel } from "./parts";
import {
  DISPLAY_WEEK,
  derivedSplit,
  fitSplit,
  MAX_DAYS,
  previewShape,
  type RoutineForm as RoutineDraft,
  resolveRoutine,
  routineIssue,
  type Same,
  splitChoices,
  splitFor,
  toggleDay,
  type WeekShape,
} from "./routine-helpers";
import { usePlanWeek, WeekStrip } from "./week-strip";

export const sameOptions = (): LabelledOption<Same>[] => [
  { description: sameAllHint(), label: sameAll(), value: "all" },
  { description: sameSomeHint(), label: sameSome(), value: "some" },
  { description: sameNoneHint(), label: sameNone(), value: "none" },
];

export interface RoutineSameFormProps {
  onChange: (value: Same) => void;
  value: Same;
}

/** Step 1: "Esses dias são sempre os mesmos?" (A2 routine.tsx:137-165). */
export function RoutineSameForm({ value, onChange }: RoutineSameFormProps) {
  return (
    <ChoiceList
      label={sameQuestion()}
      onValueChange={onChange}
      options={sameOptions()}
      value={value}
    />
  );
}

export interface RoutineDaysFormProps {
  /** How many sessions the stored config has — the preview says what
   *  changes. `null` when nothing is stored yet. */
  before: number | null;
  onChange: (value: RoutineDraft) => void;
  /** Whether the server stores a chosen split (`splitKnown`, `D216`): the
   *  picker is offered only then. */
  splitChoice: boolean;
  value: RoutineDraft;
}

const ISSUE_TEXT = {
  some_needs_both: someNeedsBoth,
  too_few: tooFew,
  too_many: tooMany,
} as const;

/**
 * Step 2: which days (or how many), and "Sua semana fica assim" — the week as
 * it WOULD be, before anything is applied (A2 routine.tsx:167-276).
 */
export function RoutineDaysForm({
  value,
  onChange,
  before,
  splitChoice,
}: RoutineDaysFormProps) {
  const { fixed, flex, total } = resolveRoutine(value);
  const issue = routineIssue(value);
  const week = usePlanWeek();
  const shape = previewShape(value.split, week.shape);
  // Every change to the days goes through `fitSplit`: a split the new day
  // count cannot hold is let go, as the server would let it go.
  const change = useCallback(
    (next: RoutineDraft) => onChange(fitSplit(next)),
    [onChange]
  );
  const setFlex = useCallback(
    (next: number) => change({ ...value, flex: next }),
    [change, value]
  );
  const setSplit = useCallback(
    (split: PreferredSplit | null) => onChange({ ...value, split }),
    [onChange, value]
  );
  return (
    <div>
      {value.same === "none" ? null : (
        <div>
          <FieldLabel meta={fixedCount({ count: fixed.length })}>
            {daysTap()}
          </FieldLabel>
          <fieldset className="m-0 grid min-w-0 grid-cols-7 gap-1.5 border-0 p-0">
            <legend className="sr-only">{daysTap()}</legend>
            {DISPLAY_WEEK.map((day) => (
              <DayToggle
                day={day}
                form={value}
                key={day}
                on={value.fixed.includes(day)}
                onChange={change}
              />
            ))}
          </fieldset>
        </div>
      )}

      {value.same === "all" ? null : (
        <div
          className={cx(
            "flex items-center justify-between gap-4 rounded-2xl bg-surface p-4 shadow",
            value.same === "some" && "mt-4"
          )}
        >
          <div className="min-w-0">
            <p className="font-semibold text-base">
              {value.same === "none" ? flexTitleNone() : flexTitleSome()}
            </p>
            <p className="text-ink-3 text-sm leading-snug">
              {value.same === "none" ? flexHintNone() : flexHintSome()}
            </p>
          </div>
          <Stepper
            label={flexLabel()}
            max={
              value.same === "none"
                ? MAX_DAYS
                : Math.max(1, MAX_DAYS - fixed.length)
            }
            min={1}
            onValueChange={setFlex}
            value={flex}
          />
        </div>
      )}

      {splitChoice && issue === null ? (
        <SplitPicker
          onChange={setSplit}
          plan={week.shape}
          total={total}
          value={value.split}
        />
      ) : null}

      <section className="mt-6">
        <FieldLabel meta={perWeek({ count: total })}>
          {previewTitle()}
        </FieldLabel>
        <div className="rounded-3xl bg-surface p-4 shadow">
          {issue === null ? (
            <>
              <WeekStrip fixed={fixed} flex={flex} shape={shape} />
              <ul className="mt-4 flex flex-col gap-2 border-line border-t pt-4">
                {splitFor(total, shape, week.custom).map((w) => (
                  <li
                    className="flex items-center gap-3 text-base"
                    key={w.letter}
                  >
                    <span className="a-display grid size-6 place-items-center rounded-full bg-surface-2 text-xs">
                      {w.letter}
                    </span>
                    <span className="font-medium">
                      {workoutLine({
                        letter: w.letter,
                        name: dayName(w),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="py-6 text-center text-base text-ink-2" role="alert">
              {ISSUE_TEXT[issue]()}
            </p>
          )}
        </div>
        {issue === null && before !== null ? (
          <p className="mt-3 flex gap-2 rounded-lg bg-accent-soft p-3.5 text-base text-ink leading-snug">
            <span className="mt-0.5 shrink-0 text-accent-ink">
              <Icon icon={ArrowRight} size={16} />
            </span>
            {total === before
              ? sameDaysNote({ count: total })
              : splitChangeNote({ after: total, before })}
          </p>
        ) : null}
      </section>
    </div>
  );
}

const SPLIT_HINTS: Readonly<Record<PreferredSplit, () => string>> = {
  full_body: splitHintFullBody,
  ppl: splitHintPpl,
  ppl_arms: splitHintPplArms,
  upper_lower: splitHintUpperLower,
};

/** `ChoiceList` values are strings; "auto" is the engine's choice, `null`. */
type SplitValue = PreferredSplit | "auto";

/**
 * "Como dividir a semana" (`D216`): the engine's choice first, then every
 * split the day count can hold — the one the engine would derive marked
 * recommended — with one honest line on why the choice is the person's
 * (`ramos-campo-2024-split`). A split the days cannot hold is not offered;
 * the engine would refuse to build it.
 */
function SplitPicker({
  total,
  value,
  plan,
  onChange,
}: {
  onChange: (split: PreferredSplit | null) => void;
  plan: WeekShape;
  total: number;
  value: PreferredSplit | null;
}) {
  const locale = getLocale();
  const derived = derivedSplit(total);
  // What "Automático" builds today: the plan's own shape at this day count.
  const auto = autoOf(plan, total);
  const options: LabelledOption<SplitValue>[] = [
    {
      description: splitAutoHint({ name: splitName(auto, locale) }),
      label: splitAuto(),
      value: "auto",
    },
    ...splitChoices(total).map((split) => ({
      description: SPLIT_HINTS[split](),
      label:
        split === derived
          ? splitRecommended({ name: splitName(split, locale) })
          : splitName(split, locale),
      value: split,
    })),
  ];
  const choose = useCallback(
    (next: SplitValue) => onChange(next === "auto" ? null : next),
    [onChange]
  );
  return (
    <section className="mt-6" data-split-picker="">
      <FieldLabel>{splitQuestion()}</FieldLabel>
      <p className="mb-3 text-ink-2 text-sm leading-snug">{splitWhy()}</p>
      <ChoiceList
        label={splitQuestion()}
        onValueChange={choose}
        options={options}
        value={value ?? "auto"}
      />
    </section>
  );
}

/**
 * The split "Automático" means for a week of `total` days: a named shape
 * that still fits, else the day count's derived one. A custom week reads as
 * the derived one too — the picker cannot write custom days.
 */
function autoOf(plan: WeekShape, total: number): PreferredSplit {
  if (plan === "by_days" || plan === "custom") {
    return derivedSplit(total);
  }
  return splitChoices(total).includes(plan) ? plan : derivedSplit(total);
}

function DayToggle({
  day,
  on,
  form,
  onChange,
}: {
  day: number;
  form: RoutineDraft;
  on: boolean;
  onChange: (value: RoutineDraft) => void;
}) {
  const locale = getLocale();
  const toggle = useCallback(
    () => onChange(toggleDay(form, day)),
    [onChange, form, day]
  );
  return (
    <button
      aria-label={weekdayLong(day, locale)}
      aria-pressed={on}
      className={cx(
        "a-press flex h-16 flex-col items-center justify-center gap-1 rounded-lg font-semibold text-sm transition-colors",
        on
          ? "bg-inverse text-on-inverse"
          : "bg-surface text-ink-2 ring-1 ring-line"
      )}
      onClick={toggle}
      type="button"
    >
      {weekdayShort(day, locale)}
      <span
        className={cx(
          "grid size-4 place-items-center rounded-full",
          on ? "bg-accent text-on-accent" : "bg-surface-2"
        )}
      >
        {on ? <Icon bold icon={Check} size={16} /> : null}
      </span>
    </button>
  );
}
